import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { cartApi, getAuthHeaders } from '@/services/api';
import { getSessionId } from '@/utils/helpers';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();
  const { t } = useTranslation();

  const fetchCart = useCallback(async () => {
    try {
      const sessionId = getSessionId();
      const headers = getAuthHeaders(token);
      const res = await cartApi.get(sessionId, headers);
      setCart(res.data);
    } catch (err) {
      console.error('Failed to fetch cart', err);
    }
  }, [token]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (productId) => {
    setLoading(true);
    try {
      const sessionId = getSessionId();
      const headers = getAuthHeaders(token);
      await cartApi.add({ product_id: productId, quantity: 1, session_id: sessionId }, headers);
      await fetchCart();
      toast.success(t('cart.addedToCart', '已添加到购物车'));
    } catch (err) {
      toast.error(t('cart.addFailed', '添加失败'));
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, quantity) => {
    try {
      const sessionId = getSessionId();
      const headers = getAuthHeaders(token);
      await cartApi.update({ product_id: productId, quantity, session_id: sessionId }, headers);
      await fetchCart();
    } catch (err) {
      toast.error(t('cart.updateFailed', '更新失败'));
    }
  };

  const removeFromCart = async (productId) => {
    try {
      const sessionId = getSessionId();
      const headers = getAuthHeaders(token);
      await cartApi.remove(productId, sessionId, headers);
      await fetchCart();
      toast.success(t('cart.removedFromCart', '已从购物车移除'));
    } catch (err) {
      toast.error(t('cart.removeFailed', '移除失败'));
    }
  };

  const clearCart = async () => {
    try {
      const sessionId = getSessionId();
      const headers = getAuthHeaders(token);
      await cartApi.clear(sessionId, headers);
      await fetchCart();
    } catch (err) {
      console.error('Failed to clear cart', err);
    }
  };

  return (
    <CartContext.Provider value={{ cart, loading, addToCart, updateQuantity, removeFromCart, clearCart, fetchCart }}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
